import type {
  NoticeBlock,
  NoticeCatalog,
  NoticeComposition,
  NoticeDiagnostic,
  NoticeStyle,
} from './notice-fidelity.models';
import {
  noticeReferenceContextMatches,
  noticeReferenceSelections,
  type NoticeReferenceSelection,
} from './notice-reference-commands';

export type NoticePageSetting = keyof NoticeComposition['page'];
export type NoticeBrandingSetting = keyof NoticeComposition['branding'];
export type NoticeDiagnosticTarget =
  | {
      kind: 'document-setting';
      section: 'page';
      property: NoticePageSetting;
      label: string;
    }
  | {
      kind: 'document-setting';
      section: 'branding';
      property: NoticeBrandingSetting;
      label: string;
    }
  | {
      kind: 'block';
      blockId: string;
      property: 'text' | 'source' | 'structure' | keyof NoticeStyle;
      label: string;
    }
  | {
      kind: 'reference';
      selection: NoticeReferenceSelection;
      property: 'binding' | 'format';
      label: string;
    }
  | {
      kind: 'table-column';
      blockId: string;
      columnId: string;
      property: 'label' | 'field' | 'format' | 'width';
      label: string;
    }
  | { kind: 'fields'; label: string; reason: string };

export interface NoticeDiagnosticResolution {
  title: string;
  affectedLabel: string;
  /** Multiple verified uses remain explicit choices; the first is never assumed. */
  targets: readonly NoticeDiagnosticTarget[];
}

export interface NoticeDiagnosticNavigation {
  diagnostic: NoticeDiagnostic;
  target: NoticeDiagnosticTarget;
}

const pageLabels: Record<NoticePageSetting, string> = {
  size: 'Page size',
  marginPt: 'Page margin',
  fontCode: 'Document font',
  fontSizePt: 'Body size',
  lineHeight: 'Line height',
};
const brandingLabels: Record<NoticeBrandingSetting, string> = {
  profile: 'Brand profile version',
  headerText: 'Header wording',
  footerText: 'Footer wording',
  accentColor: 'Accent color',
  logoCode: 'Logo',
  watermarkCode: 'Watermark',
};
const styleLabels: Record<keyof NoticeStyle, string> = {
  fontSizePt: 'Font size',
  color: 'Text color',
  backgroundColor: 'Fill color',
  borderColor: 'Border color',
  borderWidthPt: 'Border width',
  paddingPt: 'Padding',
  keepTogether: 'Keep together',
};
const issueTitles: Record<string, string> = {
  page_size: 'Choose an available page size',
  font_unavailable: 'Choose an available font',
  asset_unavailable: 'Choose an available asset',
  style_range: 'Use a supported setting value',
  color_format: 'Use a six-digit color',
  text_limit: 'Shorten this text',
  unsupported_glyph: 'Replace unsupported characters',
  page_break_position: 'Move or remove this page break',
  unknown_repeat: 'Choose a registered data list',
  unknown_field: 'Choose a registered data field',
  unknown_term: 'Choose a registered terminology token',
  format_invalid: 'Choose a permitted field format',
  optional_value_unavailable: 'A referenced value is unavailable',
  required_value: 'A required context value is unavailable',
  value_type: 'A context value has an incompatible type',
  row_unavailable: 'A referenced list row is unavailable',
  list_limit: 'A data list is unavailable or too long',
};

/** Display-only navigation. It neither repairs source nor resolves context values. */
export function resolveDiagnosticTarget(
  diagnostic: NoticeDiagnostic,
  composition: NoticeComposition | null,
  catalog: NoticeCatalog | null,
): NoticeDiagnosticResolution {
  const code = diagnostic.code.replace(
    /^(notice_fidelity|document_context)\./,
    '',
  );
  const title = issueTitles[code] ?? 'Review this source issue';
  const fallback = (
    reason = 'Inspect the retained source in Fields.',
  ): NoticeDiagnosticResolution => ({
    title,
    affectedLabel: 'Source',
    targets: [{ kind: 'fields', label: 'Fields source', reason }],
  });
  if (
    !/^(notice_fidelity|document_context)\./.test(diagnostic.code) ||
    !Object.hasOwn(issueTitles, code) ||
    !supportedComposition(composition, catalog)
  )
    return fallback();
  const source = composition!;
  const result = (
    affectedLabel: string,
    targets: NoticeDiagnosticTarget[],
  ): NoticeDiagnosticResolution =>
    targets.length ? { title, affectedLabel, targets } : fallback();
  const path = diagnostic.path;
  const setting = /^\$\.(page|branding)\.([a-zA-Z]+)$/.exec(path);
  if (setting) {
    const section = setting[1];
    const property = setting[2];
    if (section === 'page' && Object.hasOwn(pageLabels, property)) {
      const key = property as NoticePageSetting;
      return result(pageLabels[key], [
        {
          kind: 'document-setting',
          section: 'page',
          property: key,
          label: pageLabels[key],
        },
      ]);
    }
    if (section === 'branding' && Object.hasOwn(brandingLabels, property)) {
      const key = property as NoticeBrandingSetting;
      return result(brandingLabels[key], [
        {
          kind: 'document-setting',
          section: 'branding',
          property: key,
          label: brandingLabels[key],
        },
      ]);
    }
    return fallback();
  }

  const blockPath = /^\$\.blocks\[(\d+)\](.*)$/.exec(path);
  if (blockPath) {
    const index = Number(blockPath[1]);
    const block = source.blocks[index];
    if (!safeBlock(source, block)) return fallback();
    const label = blockLabel(block, index);
    const suffix = blockPath[2];
    const style = /^\.style\.([a-zA-Z]+)$/.exec(suffix);
    if (style && block.style && Object.hasOwn(styleLabels, style[1])) {
      const property = style[1] as keyof NoticeStyle;
      return result(`${label} · ${styleLabels[property]}`, [
        {
          kind: 'block',
          blockId: block.id,
          property,
          label: `${label} · ${styleLabels[property]}`,
        },
      ]);
    }
    if (!suffix && block.kind === 'pageBreak')
      return result(label, [
        { kind: 'block', blockId: block.id, property: 'structure', label },
      ]);
    if (
      suffix === '.source' &&
      block.kind === 'repeat' &&
      contextMatches(source, catalog)
    )
      return result(`${label} · Data list`, [
        {
          kind: 'block',
          blockId: block.id,
          property: 'source',
          label: `${label} · Data list`,
        },
      ]);
    const runPath = /^\.runs\[(\d+)\](?:\.(value|format))?$/.exec(suffix);
    if (
      runPath &&
      ['paragraph', 'box'].includes(block.kind) &&
      Array.isArray(block.runs)
    ) {
      const runIndex = Number(runPath[1]);
      const run = block.runs[runIndex];
      if (!run) return fallback();
      if (run.kind === 'literal' && runPath[2] !== 'format')
        return result(`${label} · Text`, [
          {
            kind: 'block',
            blockId: block.id,
            property: 'text',
            label: `${label} · Text`,
          },
        ]);
      if (run.kind === 'field' || run.kind === 'term') {
        // Renderer binding diagnostics intentionally point to the run, not its property.
        const property =
          runPath[2] === 'format' || code === 'format_invalid'
            ? 'format'
            : 'binding';
        if (
          property === 'format' &&
          (run.kind !== 'field' || !contextMatches(source, catalog))
        )
          return fallback();
        const ordinal = block.runs
          .slice(0, runIndex)
          .filter(
            (entry) => entry.kind === 'field' || entry.kind === 'term',
          ).length;
        const selection = noticeReferenceSelections(block)[ordinal];
        return result(
          `${label} · ${referenceLabel(run.value, run.kind, source, catalog)}`,
          [
            {
              kind: 'reference',
              selection,
              property,
              label: `${label} · Token ${ordinal + 1} · ${referenceLabel(run.value, run.kind, source, catalog)}`,
            },
          ],
        );
      }
      return fallback();
    }
    const columnPath =
      /^\.columns\[(\d+)\](?:\.(label|field|format|width))?$/.exec(suffix);
    if (columnPath && block.kind === 'repeat') {
      const property =
        columnPath[2] ?? (code === 'format_invalid' ? 'format' : 'field');
      if (property === 'format' && !contextMatches(source, catalog))
        return fallback();
      const target = columnTarget(
        block,
        index,
        Number(columnPath[1]),
        property as 'label' | 'field' | 'format' | 'width',
      );
      return target ? result(target.label, [target]) : fallback();
    }
    return fallback();
  }

  // Runtime diagnostics name resolved values. Locate only existing matching bindings;
  // never expose, synthesize or infer the value itself, or guess from a generic $.values.
  if (path.startsWith('$.values.') && contextMatches(source, catalog)) {
    const code = path.slice('$.values.'.length);
    const fields = catalog!.documentContext!.fields.filter(
      (field) => field.code === code,
    );
    if (fields.length !== 1) return fallback();
    const targets: NoticeDiagnosticTarget[] = [];
    source.blocks.forEach((block, index) => {
      if (
        !safeBlock(source, block) ||
        !['paragraph', 'box'].includes(block.kind)
      )
        return;
      for (const selection of noticeReferenceSelections(block)) {
        if (selection.kind === 'field' && selection.code === code)
          targets.push({
            kind: 'reference',
            selection,
            property: 'binding',
            label: `${blockLabel(block, index)} · Token ${selection.referenceIndex + 1} · ${fields[0].label}`,
          });
      }
    });
    return result(fields[0].label, targets);
  }
  if (path.startsWith('$.lists.') && contextMatches(source, catalog)) {
    const lists = catalog!.documentContext!.lists.filter(
      (list) =>
        path === `$.lists.${list.code}` ||
        path.startsWith(`$.lists.${list.code}[`),
    );
    if (lists.length !== 1) return fallback();
    const list = lists[0];
    const tail = path.slice(`$.lists.${list.code}`.length);
    const rowField = /^\[\d+\]\.([a-zA-Z][a-zA-Z0-9_.]*)$/.exec(tail);
    const field =
      rowField && list.fields.filter((entry) => entry.code === rowField[1]);
    if (tail && (!field || field.length !== 1)) return fallback();
    const targets: NoticeDiagnosticTarget[] = [];
    source.blocks.forEach((block, index) => {
      if (
        !safeBlock(source, block) ||
        block.kind !== 'repeat' ||
        block.source !== list.code
      )
        return;
      if (!field) {
        targets.push({
          kind: 'block',
          blockId: block.id,
          property: 'source',
          label: `${blockLabel(block, index)} · ${list.label}`,
        });
        return;
      }
      (block.columns ?? []).forEach((column, columnIndex) => {
        if (column.field !== field[0].code) return;
        const target = columnTarget(block, index, columnIndex, 'field');
        if (target) targets.push(target);
      });
    });
    return result(
      field ? `${list.label} · ${field[0].label}` : list.label,
      targets,
    );
  }
  return fallback();
}

function supportedComposition(
  source: NoticeComposition | null,
  catalog: NoticeCatalog | null,
): boolean {
  return !!(
    source &&
    catalog &&
    source.version === 1 &&
    ['notice-fidelity-experimental', 'notice-document-context-proof'].includes(
      source.contractCode,
    ) &&
    Array.isArray(source.blocks) &&
    source.page &&
    typeof source.page === 'object' &&
    source.branding &&
    typeof source.branding === 'object' &&
    (source.contractCode !== 'notice-document-context-proof' ||
      noticeReferenceContextMatches(source, catalog))
  );
}
function contextMatches(
  source: NoticeComposition,
  catalog: NoticeCatalog | null,
): boolean {
  return !!catalog && noticeReferenceContextMatches(source, catalog);
}
function safeBlock(
  source: NoticeComposition,
  block: NoticeBlock | undefined,
): block is NoticeBlock {
  return !!(
    block &&
    typeof block.id === 'string' &&
    /^[a-z][a-z0-9_-]{0,47}$/.test(block.id) &&
    source.blocks.filter((entry) => entry?.id === block.id).length === 1 &&
    ['paragraph', 'box', 'repeat', 'pageBreak'].includes(block.kind) &&
    (block.kind === 'pageBreak' ||
      (block.style && typeof block.style === 'object')) &&
    (!['paragraph', 'box'].includes(block.kind) ||
      (Array.isArray(block.runs) &&
        block.runs.every(
          (run) =>
            run &&
            ['literal', 'field', 'term'].includes(run.kind) &&
            typeof run.value === 'string',
        ))) &&
    (block.kind !== 'repeat' ||
      (Array.isArray(block.columns) &&
        block.columns.every(
          (column) =>
            column &&
            typeof column.id === 'string' &&
            typeof column.field === 'string',
        )))
  );
}
function blockLabel(block: NoticeBlock, index: number): string {
  const kind = {
    paragraph: 'Text',
    box: 'Box',
    repeat: 'Table',
    pageBreak: 'Page break',
  }[block.kind];
  return `${kind} ${index + 1}`;
}
function referenceLabel(
  code: string,
  kind: 'field' | 'term',
  source: NoticeComposition,
  catalog: NoticeCatalog | null,
): string {
  const entries = contextMatches(source, catalog)
    ? kind === 'field'
      ? catalog!.documentContext!.fields
      : catalog!.documentContext!.terms
    : kind === 'field'
      ? catalog?.fields
      : catalog?.terms;
  const matches = entries?.filter((entry) => entry.code === code) ?? [];
  return matches.length === 1
    ? matches[0].label
    : kind === 'field'
      ? 'Data field'
      : 'Terminology token';
}
function columnTarget(
  block: NoticeBlock,
  blockIndex: number,
  columnIndex: number,
  property: 'label' | 'field' | 'format' | 'width',
): NoticeDiagnosticTarget | null {
  const column = block.columns?.[columnIndex];
  if (
    !column ||
    typeof column.id !== 'string' ||
    !/^[a-z][a-z0-9_-]{0,47}$/.test(column.id) ||
    block.columns!.filter((entry) => entry?.id === column.id).length !== 1
  )
    return null;
  const label = {
    label: 'Heading',
    field: 'Field',
    format: 'Format',
    width: 'Width',
  }[property];
  return {
    kind: 'table-column',
    blockId: block.id,
    columnId: column.id,
    property,
    label: `${blockLabel(block, blockIndex)} · Column ${columnIndex + 1} · ${label}`,
  };
}
